import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO block. Wrap any page in this and pass title/description.
 *
 * @param {string} title          — page title (will be suffixed with "| COMPANIN")
 * @param {string} description    — 1-2 sentence summary, ≤160 chars for best CTR
 * @param {string} canonical      — full canonical URL (e.g. https://companin.com/discovery)
 * @param {string} image          — absolute OG image URL (defaults to brand image)
 * @param {string} type           — Open Graph type (default "website")
 * @param {object} jsonLd         — structured-data object that gets rendered as <script type="application/ld+json">
 * @param {boolean} noindex       — set true to send <meta name="robots" content="noindex,nofollow">
 */
export default function SEO({
  title,
  description,
  canonical,
  image = "https://companin.com/og-default.jpg",
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | COMPANIN` : "COMPANIN — Verified companions, by the hour";
  const desc =
    description ||
    "COMPANIN connects you with verified, kind companions for genuine human company — a coffee, a walk, a long talk. Hourly bookings, judgment-free.";

  // SEO fix (2026-06-29) — `canonical` + `og:url` are now emitted ONCE from
  // the build-time static HTML (frontend/scripts/generate-seo-pages.js). This
  // component used to emit them again via Helmet on hydration, giving every
  // indexed page two canonical tags — which the Semrush audit flagged on 19
  // pages. We keep the `canonical` prop in the signature so callers don't
  // break, but it's intentionally NOT rendered here. Private/noindex routes
  // don't need a canonical anyway.
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph (Facebook, LinkedIn, WhatsApp) */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="COMPANIN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* Structured data — supplied per page (e.g. Person/LocalBusiness/BreadcrumbList).
          Accepts either a single object or an array of objects → one <script> per item. */}
      {jsonLd &&
        (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((blob, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(blob)}</script>
        ))}
      {/* `canonical` prop retained for API compatibility; not rendered — see comment above. */}
      {canonical ? <meta name="companin:canonical-source" content={canonical} /> : null}
    </Helmet>
  );
}
